import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import type { User } from '@/services/types';
import { usePostHog } from 'posthog-js/react';

type UserWithRole = User & {
  role: string;
};

const roleSchema = z.object({
  role: z.string().min(1, 'Role is required')
});

type RoleFormData = z.infer<typeof roleSchema>;

interface SwitchRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithRole | null;
  onConfirm: (userId: string, newRole: string) => void;
  isLoading?: boolean;
  context: 'organisation' | 'workspace';
}

const getAvailableRoles = (context: 'organisation' | 'workspace') => {
  if (context === 'organisation') {
    return [
      { value: 'org_admin', label: 'Organisation Admin' },
      { value: 'org_user', label: 'Organisation User' }
    ];
  } else {
    return [
      { value: 'workspace_admin', label: 'Workspace Admin' },
      { value: 'workspace_user', label: 'Workspace User' }
    ];
  }
};

export function SwitchRoleModal({ 
  open, 
  onOpenChange, 
  user, 
  onConfirm, 
  isLoading = false,
  context 
}: SwitchRoleModalProps) {
  const posthog = usePostHog();

  const form = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      role: user?.role || ''
    }
  });

  const availableRoles = getAvailableRoles(context);

  const handleSubmit = async (data: RoleFormData) => {
    if (!user) return;
    posthog?.capture('user_role_switched', { user_id: user.id, new_role: data.role, old_role: user.role, context });
    posthog?.capture('modal_closed', { modal_name: 'switch_role_modal', completed: true });
    await onConfirm(user.id, data.role);
    form.reset();
    onOpenChange(false);
  };

  const handleClose = () => {
    posthog?.capture('modal_closed', { modal_name: 'switch_role_modal', completed: false });
    form.reset();
    onOpenChange(false);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Switch User Role</DialogTitle>
          <DialogDescription>
            Change the role for <span className='font-medium'>{user.fullName || user.email}</span>
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='role'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Role</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder='Select a role' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={isLoading}
                isLoading={isLoading}
                trackingEvent='role_switch_confirmed'
                trackingProperties={{ context }}
              >
                Update Role
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}